import { TellitPage } from "./components/tellit_page";
import { RadioLayout } from "@tree-company/tree-query";
import { PageComponent } from "./components/page_component";
import { CollapsibleRadio } from "./components/collapsible_radio";
import { apiClient } from "./client";
import I18n, { translatedPropertyFromKey } from "./locales";
import { NextStatement } from "./views/next_statement";

let aggregatorURL = import.meta.env.VITE_AGGREGATOR_URL;
if (aggregatorURL === undefined && import.meta.env.DEV) {
  aggregatorURL = "http://localhost:3300";
}

function getPreviousAnswer(key) {
  // We select the second to last one, since the last treesponse is the current one
  return apiClient.treesponses.at(-2)?.getAnswer(key);
}
const OPTION_KEYS = [
  "totally_agree",
  "agree",
  "neutral",
  "disagree",
  "totally_disagree",
];
const aiResponseTexts = {
  proficiencyOkToxicityOk: translatedPropertyFromKey(
    "ai_feedback.proficiency_ok_toxicity_ok",
  ),
  proficiencyOkToxicityNotOk: translatedPropertyFromKey(
    "ai_feedback.proficiency_ok_toxicity_not_ok",
  ),
  proficiencyHighToxicityOk: translatedPropertyFromKey(
    "ai_feedback.proficiency_high_toxicity_ok",
  ),
  proficiencyHighToxicityNotOk: translatedPropertyFromKey(
    "ai_feedback.proficiency_high_toxicity_not_ok",
  ),
  proficiencyLowToxicityOk: translatedPropertyFromKey(
    "ai_feedback.proficiency_low_toxicity_ok",
  ),
  proficiencyLowToxicityNotOk: translatedPropertyFromKey(
    "ai_feedback.proficiency_low_toxicity_not_ok",
  ),
};

const namePage = {
  key: "name",
  order: 4,
  component: PageComponent,
  description: translatedPropertyFromKey(["name", "intro", "description"]),
  sections: [
    {
      order: 1,
      type: "string",
      key: "firstName",
      title: translatedPropertyFromKey(["name", "firstName", "title"]),
      required: true,
      defaultValue: () => getPreviousAnswer("firstName"),
    },
    {
      order: 2,
      type: "string",
      key: "lastName",
      title: translatedPropertyFromKey(["name", "lastName", "title"]),
      required: true,
      defaultValue: () => getPreviousAnswer("lastName"),
    },
    {
      order: 3,
      type: "hidden",
      key: "completed",
      defaultValue: true,
    },
  ],
};

const sociodemoPage = {
  key: "sociodemo",
  order: 5,
  component: PageComponent,
  title: translatedPropertyFromKey(["sociodemo", "intro", "title"]),
  description: translatedPropertyFromKey(["sociodemo", "intro", "description"]),
  // We skip this page if the user answered the same name as their previous answer
  skipIf: (treesponse) => {
    return ["firstName", "lastName"].every(
      (key) => treesponse.getAnswer(key) === getPreviousAnswer(key),
    );
  },
  sections: [
    {
      order: 1,
      type: "radio",
      component: CollapsibleRadio,
      key: "gender",
      title: translatedPropertyFromKey(["sociodemo", "gender", "title"]),
      options: ["m", "v", "x"].map((value) => ({
        label: translatedPropertyFromKey([
          "sociodemo",
          "gender",
          "options",
          value,
        ]),
        value,
      })),
      defaultValue: () => getPreviousAnswer("gender"),
    },
    {
      order: 1,
      type: "number",
      key: "year_of_birth",
      title: translatedPropertyFromKey(["sociodemo", "year_of_birth", "title"]),
      options: [],
      defaultValue: () => getPreviousAnswer("year_of_birth"),
      maxLength: 4,
      placeholder: translatedPropertyFromKey([
        "sociodemo",
        "year_of_birth",
        "placeholder",
      ]),
    },
    {
      order: 3,
      type: "radio",
      component: CollapsibleRadio,
      key: "profile",
      title: translatedPropertyFromKey(["sociodemo", "profile", "title"]),
      defaultValue: () => getPreviousAnswer("profile"),
      htmlAttributes: { class: "question--profile" },
      options: ["professional", "care_giver", "care_receiver", "citizen"].map(
        (value) => ({
          label: translatedPropertyFromKey([
            "sociodemo",
            "profile",
            "options",
            value,
          ]),
          value,
        }),
      ),
    },
  ],
};

const likePage = {
  key: "like",
  order: 6,
  component: PageComponent,
  sections: [
    {
      type: "like",
      order: 1,
      key: "like",
      title: translatedPropertyFromKey(["like", "title"]),
      description: translatedPropertyFromKey(["like", "description"]),
      aggregatorSettings: {
        url: `${aggregatorURL}/cm`,
        projectKey: import.meta.env.VITE_AGGREGATOR_KEY,
      },
    },
  ],
};

const optinPage = {
  key: "optin",
  order: 7,
  component: PageComponent,
  title: translatedPropertyFromKey(["optin", "intro", "title"]),
  // We only show the opt in page if the user has not encountered it before
  skipIf: () => apiClient.treesponses.some((t) => t.page?.name === "completed"),
  sections: [
    {
      type: "checkbox",
      order: 1,
      title: translatedPropertyFromKey(["optin", "newsletter", "title"]),
      description: translatedPropertyFromKey([
        "optin",
        "newsletter",
        "description",
      ]),
      key: "newsletter",
    },
    {
      type: "checkbox",
      order: 2,
      title: translatedPropertyFromKey(["optin", "summit", "title"]),
      description: translatedPropertyFromKey([
        "optin",
        "summit",
        "description",
      ]),
      key: "summit",
    },
    {
      type: "email",
      order: 3,
      skipIf: (treesponse) =>
        treesponse.getAnswer("newsletter") !== true &&
        treesponse.getAnswer("summit") !== true,
      dependsOn: ["newsletter", "summit"],
      title: translatedPropertyFromKey(["optin", "email", "title"]),
      key: "email",
      customValidations: [
        (treesponse, _key, value) => {
          if (
            (treesponse.getAnswer("newsletter") ||
              treesponse.getAnswer("summit")) &&
            (value === null || value === undefined)
          ) {
            return [
              false,
              translatedPropertyFromKey(["optin", "email", "required"]),
            ];
          }

          return [true, null];
        },
      ],
    },
  ],
};

const nextPage = {
  key: "next",
  order: 8,
  component: NextStatement,
  sections: [],
};

export function surveySettingsForStatement(statement, i18n) {
  return {
    key: `caruna-cm-${statement}`,
    pages: [
      {
        key: "tellit",
        order: 1,
        component: TellitPage,
        sections: [
          {
            type: "radio",
            htmlAttributes: {
              class: "question--tellit",
            },
            order: 1,
            key: `opinion`,
            layout: RadioLayout.buttons,
            title: translatedPropertyFromKey(["statements", statement, "name"]),
            description: translatedPropertyFromKey([
              "statement_question",
              "description",
            ]),
            options: OPTION_KEYS.reverse().map((option) => ({
              label: translatedPropertyFromKey([
                "statement_question",
                "options",
                option,
              ]),
              value: option,
            })),
          },
        ],
      },
      {
        key: `comment-${statement}`,
        order: 3,
        component: PageComponent,
        sections: [
          {
            order: 1,
            type: "text",
            key: "comment",
            maxLength: 500,
            title: translatedPropertyFromKey(["statements", statement, "name"]),
            description: {
              nl: (treesponse) => {
                const i18n = new I18n("nl");
                const opinion = treesponse.getAnswer("opinion");
                return i18n.t([
                  "comment",
                  "description",
                  opinion || "fallback",
                ]);
              },
              fr: (treesponse) => {
                const i18n = new I18n("fr");
                const opinion = treesponse.getAnswer("opinion");
                return i18n.t([
                  "comment",
                  "description",
                  opinion || "fallback",
                ]);
              },
            },
            placeholder: translatedPropertyFromKey(["comment", "placeholder"]),
            aiResponseTexts,
            required: true,
          },
          {
            order: 99,
            type: "hidden",
            key: "bpart_title",
            dependsOn: ["opinion"],
            computeValue: (treesponse) =>
              i18n.t([
                "statement_question",
                "options",
                treesponse.getAnswer("opinion"),
              ]),
          },
        ],
      },
      namePage,
      sociodemoPage,
      likePage,
      optinPage,
      nextPage,
    ],
  };
}
